import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSecureService } from "../../store/service";
import { useAuth } from "../../hooks/useAuth";
import MainLayout from "../../layouts/Layout";
import { ExportOutlined, UserOutlined, CalendarOutlined, FilterOutlined, PlusOutlined, TeamOutlined, DownOutlined, MenuOutlined, FileOutlined } from "@ant-design/icons";
import styles from "./Projects.module.scss";
import { Routes, Route, useNavigate } from "react-router-dom";
import ProjectCard, { type ProjectCardMenuAction } from "../../components/ProjectCard/ProjectCard";
import { Button, Select, Dropdown, Input, Spin, Popover, Row, Col } from "antd";
import { useAntdAppMessage } from "../../hooks/useAntdAppMessage";
import FileDetail from "./FileDetail";
import HeaderlessLayout from "../../layouts/HeaderlessLayout";
import FooterActions from "./FooterActions";
import ApprovalModal from "../../modals/approvalModal";
import AddNewProjectModal from "../../modals/addNewProjectModal";
import { useLanguage } from "../../contexts/LanguageContext";
import type { ApiProjectAllGetScopeEnum } from "../../service/apis/ProjectApi";
import type { RevisdomAPIViewModelsEntryProjectResponse } from "../../service/models/RevisdomAPIViewModelsEntryProjectResponse";
import { formatBytes } from "../../helpers/converters";
import { useEntryDownloadProgress } from "../../hooks/useEntryDownloadProgress";
import { buildEntryEditPayload } from "../../utils/buildEntryEditPayload";
import { PageTitleInternalIcon } from "../../components/Sidebar/sidebarInlineIcons";
import ProjectFolderEmptyState from "../../components/ProjectFolderEmptyState/ProjectFolderEmptyState";
import {
  defaultSortDirectionForField,
  entryMatchesOrganizationFilter,
  getEntryChildrenCount,
  getEntryOrganizationLabel,
} from "../../utils/projectFilterSortHelpers";
import { Role, hasRole, ROLES_PROJECT_ORG_ADMIN, canSelectMultipleEntries } from "../../constants/roles";
import { formatApiUtcToLocaleDate, parseApiUtcToLocalDate } from "../../utils/apiDateTime";
import { useMarqueeSelection, type MarqueeSelectionMode } from "../../hooks/useMarqueeSelection";

const formatDate = (value: string | Date | undefined | null) => {
  if (!value) return "-";
  const s = formatApiUtcToLocaleDate(value);
  return s === "-" ? "-" : s;
};

// Efektif tarih: updatedAt varsa onu, yoksa createdAt
const getEffectiveDate = (project: any): Date | null => {
  const value = project?.updatedAt ?? project?.createdAt;
  return parseApiUtcToLocalDate(value);
};

const PAGE_TITLE_ICON_SIZE = { fontSize: 20, width: 20, height: 20 } as const;

type ProjectSelectionDetail = {
  id: number;
  name: string;
  entryType: string;
  status?: string;
  share?: any[];
  thumbnail?: any;
  path?: string;
  fileUrl?: string;
};

function projectToSelectionDetail(project: any): ProjectSelectionDetail {
  return {
    id: project.id,
    name: project.name,
    entryType: "Project",
    status: project.status,
    share: project.share,
    thumbnail: project.thumbnail,
    path: project.path,
  };
}

interface ProjectsProps {
  scope?: ApiProjectAllGetScopeEnum;
  pageTitle?: string;
}

const Projects: React.FC<ProjectsProps> = ({ scope = 'Internal', pageTitle = 'Internal Projects' }) => {
  const message = useAntdAppMessage();
  const { user, canSearchUserEmails } = useAuth();
  const { getCardDownloadProgress, downloadEntry, downloadEntries } = useEntryDownloadProgress();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const {
    getProjects,
    deleteEntry,
    changeStatus,
    editProject,
    toggleProjectPin,
    getAllUserEmails,
  } = useSecureService();
  const [projects, setProjects] = useState<any[]>([]);
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
  const [, setSharedProjects] = useState<any[]>([]);
  // Filter & Sort (proje listesinde status yok; klasör detayında ProjectDetail ayrı)
  const [filterDateType, setFilterDateType] = useState<string>('all');
  const [filterOrganization, setFilterOrganization] = useState('');
  const [filterName, setFilterName] = useState('');
  const [sortField, setSortField] = useState<
    'creationDate' | 'organization' | 'name' | 'numberOfFiles' | ''
  >('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | ''>('');
  const [openFilterDropdown, setOpenFilterDropdown] = useState(false);
  const [nameEmailSuggestions, setNameEmailSuggestions] = useState<string[]>([]);
  const [nameEmailSuggestionsLoading, setNameEmailSuggestionsLoading] = useState(false);
  const [nameEmailPopoverOpen, setNameEmailPopoverOpen] = useState(false);
  const [openSortDropdown, setOpenSortDropdown] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [changeCoverTarget, setChangeCoverTarget] = useState<any>(null);
  const changeCoverInputRef = useRef<HTMLInputElement>(null);
  const getAllUserEmailsRef = useRef(getAllUserEmails);
  getAllUserEmailsRef.current = getAllUserEmails;

  // FooterActions için state'ler
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [selectedItemsDetails, setSelectedItemsDetails] = useState<ProjectSelectionDetail[]>([]);
  const rangeSelectionAnchorIdRef = useRef<number | null>(null);
  const marqueeBaseSelectionRef = useRef<Set<number>>(new Set());
  const suppressCardNavigationRef = useRef(false);
  /** Kart menüsünden Assign/Delete: checkbox seçmeden modal hedefi */
  const [assignDeleteFromMenu, setAssignDeleteFromMenu] = useState<ProjectSelectionDetail[] | null>(null);
  // Approval modal state'leri
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const projectsBasePath = scope === 'External' ? '/external-projects' : '/internal-projects';
  const isExternalScope = scope === 'External';
  const sortSelectPopupClassName = `${styles.projectDetailSortSelectPopup}${isExternalScope ? ` ${styles.projectDetailSortSelectPopupExternal}` : ''}`;
  const canCreateProject = !!user && hasRole(user.role, ...ROLES_PROJECT_ORG_ADMIN);

  const pageHeaderIcon = isExternalScope ? (
    <ExportOutlined style={{ color: 'currentColor', ...PAGE_TITLE_ICON_SIZE }} />
  ) : (
    <PageTitleInternalIcon style={PAGE_TITLE_ICON_SIZE} />
  );

  useEffect(() => {
    localStorage.setItem('lastProjectsBasePath', projectsBasePath);
    const fetchProjects = async () => {
      setProjectsLoading(true);
      try {
        const response = await getProjects(scope);
        setProjects(response?.data?.items || []);
      } finally {
        setProjectsLoading(false);
      }
    };
    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getProjects stable, scope/projectsBasePath trigger refetch
  }, [scope, projectsBasePath]);

  useEffect(() => {
    projects.forEach(project => {
      if(project.shared) {
        setSharedProjects(project);
      }
    });
  }, [projects]);

  useEffect(() => {
    if (!openFilterDropdown) {
      setNameEmailSuggestions([]);
      setNameEmailSuggestionsLoading(false);
      setNameEmailPopoverOpen(false);
      return;
    }
    const term = filterName.trim();
    if (!term) {
      setNameEmailSuggestions([]);
      setNameEmailSuggestionsLoading(false);
      return;
    }
    if (!canSearchUserEmails) {
      setNameEmailSuggestions([]);
      setNameEmailSuggestionsLoading(false);
      return;
    }
    let cancelled = false;
    setNameEmailSuggestionsLoading(true);
    const timer = window.setTimeout(async () => {
      const res = await getAllUserEmailsRef.current(term);
      if (cancelled) return;
      setNameEmailSuggestionsLoading(false);
      if (res.success && Array.isArray(res.data)) {
        setNameEmailSuggestions(res.data);
      } else {
        setNameEmailSuggestions([]);
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [filterName, openFilterDropdown, canSearchUserEmails]);

  /** Footer’dan proje/klasör/dosya düzenleme veya paylaşım sonrası kartların güncellenmesi */
  const handleFooterRefreshProjects = useCallback(async () => {
    try {
      const response = await getProjects(scope);
      const items: RevisdomAPIViewModelsEntryProjectResponse[] = response?.data?.items || [];
      setProjects(items);
      setSelectedItemsDetails((prev) =>
        prev.map((detail) => {
          if (detail.entryType !== "Project") return detail;
          const p = items.find((x) => x.id === detail.id);
          if (!p) return detail;
          const id = p.id ?? detail.id;
          return {
            id,
            name: p.name ?? detail.name,
            entryType: "Project",
            status: p.status as string | undefined,
            share: p.share ?? undefined,
            thumbnail: p.thumbnailUrl ?? detail.thumbnail,
            path: p.path ?? detail.path,
          };
        })
      );
    } catch {
      /* ignore */
    }
  }, [scope, getProjects]);

  // Pinned projeler filtreye dahil edilmez, her zaman en üstte. Filtre ve sıralama sadece pinned olmayanlara uygulanır.
  const projectsToRender = useMemo(() => {
    const pinned = projects.filter((p: any) => p?.isPinned === true);
    const unpinned = projects.filter((p: any) => p?.isPinned !== true);

    const now = new Date();
    const inCurrentMonth = (d: Date) => d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    const inCurrentQuarter = (d: Date) => {
      const q = Math.floor(d.getMonth() / 3) + 1;
      const nq = Math.floor(now.getMonth() / 3) + 1;
      return d.getFullYear() === now.getFullYear() && q === nq;
    };
    const inCurrentYear = (d: Date) => d.getFullYear() === now.getFullYear();

    let list = unpinned.filter((project: any) => {
      if (filterDateType && filterDateType !== 'all') {
        const d = getEffectiveDate(project);
        if (!d) return false;
        if (filterDateType === 'month' && !inCurrentMonth(d)) return false;
        if (filterDateType === 'quarter' && !inCurrentQuarter(d)) return false;
        if (filterDateType === 'year' && !inCurrentYear(d)) return false;
      }
      if (!entryMatchesOrganizationFilter(project, filterOrganization.trim())) return false;
      if (filterName.trim().length >= 2) {
        const q = filterName.trim().toLowerCase();
        const nameMatch = String(project.name || '').toLowerCase().includes(q);
        const shareMatch = (project.share || []).some((u: { email?: string }) =>
          String(u?.email || '').toLowerCase().includes(q)
        );
        if (!nameMatch && !shareMatch) return false;
      }
      return true;
    });

    const effectiveDir = sortDirection || 'desc';
    const mult = effectiveDir === 'asc' ? 1 : -1;

    if (sortField) {
      const sorted = [...list];
      if (sortField === 'creationDate') sorted.sort((a, b) => mult * ((getEffectiveDate(a)?.getTime() ?? 0) - (getEffectiveDate(b)?.getTime() ?? 0)));
      else if (sortField === 'organization')
        sorted.sort((a, b) => mult * getEntryOrganizationLabel(a).localeCompare(getEntryOrganizationLabel(b)));
      else if (sortField === 'name') sorted.sort((a, b) => mult * ((a.name || '').localeCompare(b.name || '')));
      else if (sortField === 'numberOfFiles')
        sorted.sort((a, b) => mult * (getEntryChildrenCount(a) - getEntryChildrenCount(b)));
      list = sorted;
    }

    return [...pinned, ...list];
  }, [projects, filterDateType, filterOrganization, filterName, sortField, sortDirection]);

  const totalSizeBytes = useMemo(
    () => projectsToRender.reduce((sum, p) => sum + (p?.totalSizeInBytes ?? 0), 0),
    [projectsToRender]
  );

  const canSelectMultiple = useMemo(() => canSelectMultipleEntries(user?.role), [user?.role]);

  useEffect(() => {
    if (selectedItems.size === 0) {
      rangeSelectionAnchorIdRef.current = null;
    }
  }, [selectedItems.size]);

  const applyMarqueeSelection = useCallback(
    (ids: number[], mode: MarqueeSelectionMode) => {
      if (!canSelectMultiple) return;
      setAssignDeleteFromMenu(null);

      const allowedIds = ids.filter((id) => projectsToRender.some((project) => project.id === id));
      const nextIds =
        mode === "add"
          ? new Set([...Array.from(marqueeBaseSelectionRef.current), ...allowedIds])
          : new Set(allowedIds);

      setSelectedItems(nextIds);
      setSelectedItemsDetails(
        projectsToRender.filter((project) => nextIds.has(project.id)).map(projectToSelectionDetail)
      );
      rangeSelectionAnchorIdRef.current = allowedIds[0] ?? null;
    },
    [canSelectMultiple, projectsToRender]
  );

  const {
    setContainerRef: setMarqueeContainerRef,
    marqueeRect,
  } = useMarqueeSelection({
    enabled: canSelectMultiple && editingProjectId == null,
    onMarqueeStart: (mode) => {
      if (mode === "add") {
        marqueeBaseSelectionRef.current = new Set(selectedItems);
      } else {
        marqueeBaseSelectionRef.current = new Set();
      }
    },
    onSelectionPreview: applyMarqueeSelection,
    onSelectionCommit: applyMarqueeSelection,
    onClearSelection: () => {
      if (!canSelectMultiple) return;
      setAssignDeleteFromMenu(null);
      setSelectedItems(new Set());
      setSelectedItemsDetails([]);
      rangeSelectionAnchorIdRef.current = null;
    },
    onMarqueeGestureComplete: () => {
      suppressCardNavigationRef.current = true;
    },
  });

  // FooterActions için handler fonksiyonları
  const handleChangeCoverFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !changeCoverTarget) return;
    const shareEmails = (changeCoverTarget.share || []).map((s: any) => typeof s === 'string' ? s : s?.email).filter(Boolean);
    try {
      const result = await editProject({
        id: changeCoverTarget.id,
        name: changeCoverTarget.name,
        thumbnail: file,
        share: shareEmails
      });
      if (result?.success) {
        message.success('Cover updated.');
        const response = await getProjects(scope);
        setProjects(response?.data?.items || []);
      } else {
        message.error(result?.message || 'Failed to update cover.');
      }
    } catch {
      message.error('An error occurred while updating cover.');
    }
    setChangeCoverTarget(null);
  };

  const handleDelete = async () => {
    try {
      const fromMenu = assignDeleteFromMenu?.length ? assignDeleteFromMenu : null;
      const details = fromMenu ?? selectedItemsDetails.filter((d) => selectedItems.has(d.id));
      if (details.length === 0) return;
      const entriesToDelete = details.map((d) => ({
        id: d.id,
        entryType: 'Project' as const,
      }));
      await deleteEntry(entriesToDelete);
      message.success('Selected projects deleted successfully.');
      const response = await getProjects(scope);
      setProjects(response?.data?.items || []);
      setSelectedItems(new Set());
      setSelectedItemsDetails([]);
      setAssignDeleteFromMenu(null);
    } catch (error) {
      message.error('An error occurred during deletion.');
    }
  };

  const handleDownload = async () => {
    if (!selectedItems || selectedItems.size === 0) return;
    try {
      await downloadEntries(
        selectedItemsDetails.map((item) => ({
          id: item.id,
          path: item.path,
          entryType: item.entryType,
        }))
      );
      message.success('Download started.');
    } catch (error) {
      console.error('[Download] handleDownload error:', error);
      message.error('Download failed. Please try again.');
    }
  };

  const handleCopyUrl = () => {
    // Seçili item'ların URL'lerini kopyala
    let urlsToCopy = '';
    
    if (selectedItems && selectedItems.size > 0) {
      // Seçili item'ların URL'lerini oluştur
      const baseUrl = window.location.origin;
      urlsToCopy = selectedItemsDetails?.map(item => {
        if (item.entryType === 'Project') {
          return `${baseUrl}/${item.path}`;
        } else if (item.entryType === 'File') {
          return `${baseUrl}/${item.path}`;
        } else if (item.entryType === 'Folder') {
          return `${baseUrl}/${item.path}`;
        }
        return '';
      }).filter(url => url !== '').join('\n');
    } else {
      // Hiçbir şey seçili değilse mevcut URL'i kopyala
      urlsToCopy = window.location.href;
    }
    
    navigator.clipboard.writeText(urlsToCopy);
          message.success('URL copied.');
  };

  const handleCompare = () => {
    // Compare işlemi - şimdilik boş
  };

  // Approval modal fonksiyonları
  const handleApprovalConfirm = async (_changeSubentries: boolean) => {
    setApprovalLoading(true);
    try {
      // Seçili projelerin statüsünü "Approved" olarak değiştir
      if (selectedItems.size > 0) {
        const entriesToChange = selectedItemsDetails.map(item => ({
          id: item.id,
          entryType: item.entryType,
          entryStatus: 'Approved',
          changeSubentries: true,
        }));
        
        // changeStatus API'sini çağır
        const response = await changeStatus(entriesToChange);
        
        if (response.success) {
          message.success('Status changed successfully.');
          // Projeleri yeniden yükle
          const projectsResponse = await getProjects(scope);
          setProjects(projectsResponse?.data?.items || []);
          setSelectedItems(new Set());
          setSelectedItemsDetails([]);
        } else {
          throw new Error(response.message || 'Status change failed');
        }
      }
      
      // Modal'ı kapat
      setIsApprovalModalVisible(false);
    } catch (error) {
      message.error('Failed to change status. Please try again.');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApprovalCancel = () => {
    setIsApprovalModalVisible(false);
  };

  const handleProjectSelect = (
    project: any,
    isSelected: boolean,
    meta?: { shiftKey?: boolean }
  ) => {
    const shiftKey = Boolean(meta?.shiftKey);

    if (isSelected) {
      setAssignDeleteFromMenu(null);

      if (shiftKey && canSelectMultiple) {
        let anchorId = rangeSelectionAnchorIdRef.current;
        if (anchorId == null && selectedItems.size > 0) {
          const visibleIds = projectsToRender.map((p) => p.id);
          anchorId = visibleIds.find((id) => selectedItems.has(id)) ?? null;
        }

        if (anchorId != null) {
          const visibleIds = projectsToRender.map((p) => p.id);
          const anchorIdx = visibleIds.indexOf(anchorId);
          const clickIdx = visibleIds.indexOf(project.id);
          if (anchorIdx >= 0 && clickIdx >= 0) {
            const start = Math.min(anchorIdx, clickIdx);
            const end = Math.max(anchorIdx, clickIdx);
            const rangeIds = visibleIds.slice(start, end + 1);
            setSelectedItems(new Set(rangeIds));
            setSelectedItemsDetails(
              projectsToRender
                .filter((p) => rangeIds.includes(p.id))
                .map(projectToSelectionDetail)
            );
            rangeSelectionAnchorIdRef.current = anchorId;
            return;
          }
        }
      }

      if (selectedItems.size > 0 && !canSelectMultiple) {
        setSelectedItems(new Set([project.id]));
        setSelectedItemsDetails([projectToSelectionDetail(project)]);
        rangeSelectionAnchorIdRef.current = project.id;
        return;
      }

      setSelectedItems((prev) => new Set([...Array.from(prev), project.id]));
      setSelectedItemsDetails((prev) => {
        if (prev.some((item) => item.id === project.id)) return prev;
        return [...prev, projectToSelectionDetail(project)];
      });
      rangeSelectionAnchorIdRef.current = project.id;
    } else {
      setSelectedItems((prev) => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(project.id);
        return newSet;
      });
      setSelectedItemsDetails((prev) => prev.filter((item) => item.id !== project.id));
      rangeSelectionAnchorIdRef.current = project.id;
    }
  };

  return (
    <Routes>
      <Route
        path=":id/file/:fileId"
        element={
          <HeaderlessLayout title="File" icon={<FileOutlined />} forceInitialCollapse>
            <FileDetail breadcrumbPathParts={[]} bclocation={null} />
          </HeaderlessLayout>
        }
      />
      <Route path="" element={
        <MainLayout
          title={pageTitle || t('projects.title')}
          icon={pageHeaderIcon}
          topBarActions={canCreateProject ? (
            <Button
              type="primary"
              className={styles.newProjectTopBarButton}
              icon={<PlusOutlined />}
              onClick={() => setIsProjectModalOpen(true)}
            >
              New Project
            </Button>
          ) : undefined}
        >
          <div
            ref={setMarqueeContainerRef}
            data-marquee-root
            className={styles.projectsMarqueeRoot}
          >
            {marqueeRect
              ? createPortal(
                  <div
                    className={styles.projectDetailMarqueeSelection}
                    style={{
                      left: marqueeRect.left,
                      top: marqueeRect.top,
                      width: marqueeRect.width,
                      height: marqueeRect.height,
                    }}
                    aria-hidden
                  />,
                  document.body
                )
              : null}
          <div className={styles.projectDetailToolbar} data-marquee-ignore="true">
            <div className={styles.projectDetailToolbarSegment}>
            <Dropdown
              trigger={['click']}
              open={openFilterDropdown}
              onOpenChange={(open) => {
                setOpenFilterDropdown(open);
                if (!open) {
                  setNameEmailPopoverOpen(false);
                  setNameEmailSuggestions([]);
                  setNameEmailSuggestionsLoading(false);
                }
                if (open) setOpenSortDropdown(false);
              }}
              getPopupContainer={() => document.body}
              align={{ offset: [0, 10] }}
              overlayClassName={`${styles.projectDetailFilterDropdownOverlay}${isExternalScope ? ` ${styles.projectDetailFilterDropdownOverlayExternal}` : ''}`}
              dropdownRender={() => (
                <div
                  className={`${styles.projectDetailFilterDropdown} ${isExternalScope ? styles.projectDetailFilterDropdownExternal : ''}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className={styles.projectDetailFilterRow}>
                    <CalendarOutlined className={styles.projectDetailFilterIcon} />
                    <span className={styles.projectDetailFilterLabel}>{t('projectDetail.date')}</span>
                    <div className={styles.projectDetailFilterControlShell}>
                      <Select
                        className={styles.projectDetailFilterSelect}
                        popupClassName={sortSelectPopupClassName}
                        getPopupContainer={() => document.body}
                        placeholder={t('projectDetail.date')}
                        value={filterDateType}
                        onChange={(v) => setFilterDateType(v ?? 'all')}
                        suffixIcon={<DownOutlined />}
                        options={[
                          { value: 'all', label: t('projectDetail.dateAll') },
                          { value: 'month', label: t('projectDetail.dateMonth') },
                          { value: 'quarter', label: t('projectDetail.dateQuarter') },
                          { value: 'year', label: t('projectDetail.dateYear') },
                        ]}
                      />
                    </div>
                  </div>
                  <div className={styles.projectDetailFilterRow}>
                    <TeamOutlined className={styles.projectDetailFilterIcon} />
                    <span className={styles.projectDetailFilterLabel}>{t('projectDetail.organization')}</span>
                    <div className={styles.projectDetailFilterControlShell}>
                      <Input
                        className={styles.projectDetailFilterInput}
                        placeholder={t('projectDetail.organizationName')}
                        value={filterOrganization}
                        onChange={(e) => setFilterOrganization(e.target.value)}
                        allowClear
                      />
                    </div>
                  </div>
                  <div className={styles.projectDetailFilterRow}>
                    <UserOutlined className={styles.projectDetailFilterIcon} />
                    <span className={styles.projectDetailFilterLabel}>{t('projectDetail.name')}</span>
                    <Popover
                      styles={{ root: { flex: 1, minWidth: 0 } }}
                      open={
                        openFilterDropdown &&
                        nameEmailPopoverOpen &&
                        filterName.trim().length >= 1
                      }
                      onOpenChange={setNameEmailPopoverOpen}
                      placement="bottomLeft"
                      trigger={[]}
                      destroyTooltipOnHide
                      getPopupContainer={() => document.body}
                      overlayClassName={`${styles.projectDetailNameEmailPopover}${isExternalScope ? ` ${styles.projectDetailNameEmailPopoverExternal}` : ''}`}
                      content={
                        <div className={styles.projectDetailNameEmailSuggestions}>
                          {nameEmailSuggestionsLoading ? (
                            <div className={styles.projectDetailNameEmailSuggestionsStatus}>
                              <Spin size="small" />
                            </div>
                          ) : nameEmailSuggestions.length === 0 ? (
                            <div className={styles.projectDetailNameEmailSuggestionsStatus}>
                              {t('projectDetail.emailSuggestEmpty')}
                            </div>
                          ) : (
                            <ul className={styles.projectDetailNameEmailSuggestionsList}>
                              {nameEmailSuggestions.map((email) => (
                                <li key={email}>
                                  <button
                                    type="button"
                                    className={styles.projectDetailNameEmailSuggestionItem}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      setFilterName(email);
                                      setNameEmailPopoverOpen(false);
                                    }}
                                  >
                                    {email}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      }
                    >
                      <div className={styles.projectDetailFilterControlShell}>
                        <Input
                          className={styles.projectDetailFilterInput}
                          placeholder={t('projectDetail.userName')}
                          value={filterName}
                          onChange={(e) => {
                            setFilterName(e.target.value);
                            setNameEmailPopoverOpen(true);
                          }}
                          onFocus={() => setNameEmailPopoverOpen(true)}
                          allowClear
                        />
                      </div>
                    </Popover>
                  </div>
                </div>
              )}
            >
              <Button type="default" className={`${styles.projectDetailToolbarButton} ${(openFilterDropdown || (filterDateType && filterDateType !== 'all') || !!filterOrganization.trim() || !!filterName.trim()) ? styles.projectDetailToolbarButtonActive : ''}`}>
                <span className={styles.projectDetailToolbarButtonContent}>
                  <FilterOutlined />
                  <span>{t('common.filter')}</span>
                </span>
              </Button>
            </Dropdown>
            <Dropdown
              trigger={['click']}
              open={openSortDropdown}
              onOpenChange={(open) => { setOpenSortDropdown(open); if (open) setOpenFilterDropdown(false); }}
              getPopupContainer={() => document.body}
              align={{ offset: [0, 10] }}
              overlayClassName={`${styles.projectDetailSortDropdownOverlay}${isExternalScope ? ` ${styles.projectDetailSortDropdownOverlayExternal}` : ''}`}
              dropdownRender={() => {
                const directionOptions = !sortField ? [] : sortField === 'creationDate'
                  ? [{ value: 'desc', label: t('projectDetail.sortDirNewToOld') }, { value: 'asc', label: t('projectDetail.sortDirOldToNew') }]
                  : sortField === 'organization' || sortField === 'name'
                    ? [{ value: 'asc', label: t('projectDetail.sortDirAToZ') }, { value: 'desc', label: t('projectDetail.sortDirZToA') }]
                    : [{ value: 'asc', label: t('projectDetail.sortDirAsc') }, { value: 'desc', label: t('projectDetail.sortDirDesc') }];
                return (
                <div
                  className={`${styles.projectDetailSortDropdown} ${isExternalScope ? styles.projectDetailSortDropdownExternal : ''}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className={styles.projectDetailSortDropdownRow}>
                    <div className={styles.projectDetailFilterControlShell}>
                      <Select
                        className={styles.projectDetailSortSelect}
                        popupClassName={sortSelectPopupClassName}
                        getPopupContainer={() => document.body}
                        value={sortField || undefined}
                        onChange={(v) => {
                          const next = (v ?? '') as typeof sortField;
                          setSortField(next);
                          setSortDirection(defaultSortDirectionForField(next));
                        }}
                        placeholder={t('projectDetail.sortBy')}
                        allowClear
                        suffixIcon={<DownOutlined />}
                        options={[
                          { value: 'creationDate', label: t('projectDetail.sortFieldCreationDate') },
                          { value: 'organization', label: t('projectDetail.sortFieldOrganization') },
                          { value: 'name', label: t('projectDetail.sortFieldName') },
                          { value: 'numberOfFiles', label: t('projectDetail.sortFieldNumberOfFiles') },
                        ]}
                      />
                    </div>
                    <div className={styles.projectDetailFilterControlShell}>
                      <Select
                        className={styles.projectDetailSortSelect}
                        popupClassName={sortSelectPopupClassName}
                        getPopupContainer={() => document.body}
                        value={sortDirection || undefined}
                        onChange={(v) => setSortDirection((v ?? '') as 'asc' | 'desc')}
                        placeholder={t('projectDetail.sortDirNewToOld')}
                        allowClear
                        disabled={!sortField}
                        suffixIcon={<DownOutlined />}
                        options={directionOptions}
                      />
                    </div>
                  </div>
                </div>
                );
              }}
            >
              <Button type="default" className={`${styles.projectDetailToolbarButton} ${(openSortDropdown || !!sortField) ? styles.projectDetailToolbarButtonActive : ''}`}>
                <span className={styles.projectDetailToolbarButtonContent}>
                  <MenuOutlined />
                  <span>{t('projectDetail.sortBy')}</span>
                </span>
              </Button>
            </Dropdown>
            </div>
            <span className={styles.projectDetailToolbarCount}>
              {projectsToRender.length} {t('projectDetail.totalProjectCount')} <span className={styles.projectDetailToolbarSize}>{formatBytes(totalSizeBytes)}</span>
            </span>
          </div>
          <div className={styles.projectGridWrapper}>
            <Spin spinning={projectsLoading} size="large" className={styles.projectsSpin}>
            {!projectsLoading && projects.length === 0 && !canCreateProject ? (
              <Row className={styles.projectsListEmptyWrap} justify="center" align="top">
                <Col xs={24} style={{ display: "flex", justifyContent: "center" }}>
                  <ProjectFolderEmptyState
                    variant={isExternalScope ? "external" : "internal"}
                    messageOnly
                    useEmptyIllustration
                    plain
                    title={t("projects.noProjectsYetTitle")}
                    hint={t("projects.noProjectsYetHint")}
                  />
                </Col>
              </Row>
            ) : (
            <div className={styles.projectCardsGrid}>
            {projectsToRender.map((project) => (
              <div
                key={project.id}
                className={styles.projectGridCol}
                data-marquee-entry-id={project.id}
              >
                <ProjectCard
                  project={project}
                  projectsBasePath={projectsBasePath}
                  variant={scope === 'External' ? 'external' : 'internal'}
                  isSelected={selectedItems.has(project.id)}
                  onSelectChange={(checked, meta) => handleProjectSelect(project, checked, meta)}
                  formatDate={formatDate}
                  showCheckbox={user?.role !== Role.Viewer}
                  canMultiSelect={canSelectMultiple}
                  canTogglePin={!!canCreateProject}
                  downloadProgress={getCardDownloadProgress(project.id)}
                  selectedCount={selectedItems.size}
                  disabled={selectedItems.size > 0 && !selectedItems.has(project.id) && !canSelectMultiple}
                  editingProjectId={editingProjectId}
                  shouldSuppressNavigation={() => {
                    if (suppressCardNavigationRef.current) {
                      suppressCardNavigationRef.current = false;
                      return true;
                    }
                    return false;
                  }}
                  onPinClick={async (proj) => {
                    try {
                      const result = await toggleProjectPin(proj.id);
                      if (result?.success && result?.data) {
                        setProjects((prev) =>
                          prev.map((p) => (p.id === result.data!.id ? { ...p, ...result.data } : p))
                        );
                      }
                    } catch {
                      message.error('Failed to update pin.');
                    }
                  }}
                  onRenameSubmit={async (proj, newName) => {
                    const trimmed = newName.trim();
                    if (!trimmed) {
                      setEditingProjectId(null);
                      return;
                    }
                    try {
                      await editProject(buildEntryEditPayload(proj, { name: trimmed }));
                      message.success('Project renamed.');
                      const response = await getProjects(scope);
                      setProjects(response?.data?.items || []);
                      setEditingProjectId(null);
                    } catch {
                      message.error('An error occurred while renaming.');
                      setEditingProjectId(null);
                    }
                  }}
                  onRenameCancel={() => setEditingProjectId(null)}
                  onMenuAction={async (action: ProjectCardMenuAction) => {
                    if (action === 'rename') {
                      setEditingProjectId(project.id);
                      return;
                    }
                    if (action === 'delete') {
                      setAssignDeleteFromMenu([{
                        id: project.id,
                        name: project.name,
                        entryType: 'Project',
                        status: project.status,
                        share: project.share,
                        thumbnail: project.thumbnail,
                        path: project.path
                      }]);
                      setOpenDeleteModal(true);
                    }
                    if (action === 'share') {
                      setAssignDeleteFromMenu([{
                        id: project.id,
                        name: project.name,
                        entryType: 'Project',
                        status: project.status,
                        share: project.share,
                        thumbnail: project.thumbnail,
                        path: project.path
                      }]);
                      setOpenShareModal(true);
                    }
                    if (action === 'changeCover') {
                      setChangeCoverTarget(project);
                      setTimeout(() => changeCoverInputRef.current?.click(), 0);
                    }
                    if (action === 'download') {
                      try {
                        await downloadEntry({
                          id: project.id,
                          path: project.path,
                          entryType: 'Project',
                        });
                      } catch (e) {
                        console.error('[Download] Card menu error:', e);
                      }
                    }
                  }}
                />
              </div>
            ))}
            {canCreateProject && (
              <div className={styles.projectGridCol}>
                <button
                  type="button"
                  className={`${styles.createProjectCard} ${scope === 'External' ? styles.createProjectCardExternal : ''}`}
                  onClick={() => setIsProjectModalOpen(true)}
                  aria-label="Create new project"
                >
                  <PlusOutlined className={styles.createProjectCardIcon} />
                  <span className={styles.createProjectCardText}>Create new project</span>
                </button>
              </div>
            )}
            </div>
            )}
            </Spin>
          </div>
          </div>

          {/* FooterActions — toplu seçim veya kart menüsünden Assign/Delete */}
          {(selectedItems.size > 0 || assignDeleteFromMenu) && (
            <FooterActions
              onDelete={handleDelete}
              onDownload={handleDownload}
              onCopyUrl={handleCopyUrl}
              onCompare={handleCompare}
              onStatusChange={handleFooterRefreshProjects}
              onApprovalRequest={() => setIsApprovalModalVisible(true)} // Approval modal'ı aç
              modalsOnly={selectedItems.size === 0 && !!assignDeleteFromMenu}
              onShareOrDeleteModalClosed={() => setAssignDeleteFromMenu(null)}
              selectedItems={
                assignDeleteFromMenu?.length
                  ? new Set(assignDeleteFromMenu.map((d) => d.id))
                  : selectedItems
              }
              selectedItemsDetails={assignDeleteFromMenu ?? selectedItemsDetails}
              currentEntry={(assignDeleteFromMenu ?? selectedItemsDetails)?.[0]}
              openShareModal={openShareModal}
              onShareModalOpenChange={setOpenShareModal}
              openDeleteModal={openDeleteModal}
              onDeleteModalOpenChange={setOpenDeleteModal}
              variant={scope === 'External' ? 'external' : 'internal'}
              style={{
                transform: 'translateY(0)',
                transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
                opacity: 1
              }}
            />
          )}
          
          {/* Footer Actions için safe alan */}
          <div className={styles.footerSafeArea} />
          
          {/* Approval Modal */}
          <ApprovalModal
            visible={isApprovalModalVisible}
            onCancel={handleApprovalCancel}
            onConfirm={handleApprovalConfirm}
            loading={approvalLoading}
            variant={scope === 'External' ? 'external' : 'internal'}
          />

          <input
            ref={changeCoverInputRef}
            id="projects-change-cover-input"
            name="changeCover"
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml,image/bmp"
            style={{ display: 'none' }}
            onChange={handleChangeCoverFile}
          />
          <AddNewProjectModal
            isOpen={isProjectModalOpen}
            onClose={() => setIsProjectModalOpen(false)}
            defaultScope={scope}
            variant={scope === 'External' ? 'external' : 'internal'}
            onSuccess={(path) => {
              setIsProjectModalOpen(false);
              if (path) {
                navigate(`/${path}`, { state: { fromProjectsBasePath: projectsBasePath } });
                return;
              }
              getProjects(scope).then((response) => {
                setProjects(response?.data?.items || []);
              });
            }}
          />
        </MainLayout>
      } />
    </Routes>
  );
};

export default Projects;